import quotationRequestService from "../../../../services/procurementService/quotationRequestService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_QUOTATIONREQUESTS } from "../../../../utils/constants";
import { QuotationRequestInterface } from "../../../../models/ProcurementModule/QuotationRequest";
import ToastMessage from "../../../../common/ToastMessage";

interface UpdateQuotationRequestContext {
  previousQuotationRequest: QuotationRequestInterface[];
}

const useUpdateQuotationRequest = (
  purchaseOrderId: string | undefined,
  page: number,
  pageSize: number,
  fileNames: {
    fileUrl: string;
    fileName: string;
  }[]
) => {
  const queryClient = useQueryClient();

  return useMutation<
    QuotationRequestInterface,
    Error,
    QuotationRequestInterface,
    UpdateQuotationRequestContext
  >({
    mutationFn: async (vendor: QuotationRequestInterface) => {
      // Assuming the ID is stored in the vendor object or can be derived
      return quotationRequestService.Update(vendor, fileNames);
    },

    onMutate: async (updatedData: QuotationRequestInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_QUOTATIONREQUESTS,
          purchaseOrderId,
          page,
          pageSize,
        ],
      });

      const previousQuotationRequest =
        queryClient.getQueryData<QuotationRequestInterface[]>([
          CACHE_KEY_QUOTATIONREQUESTS,
          purchaseOrderId,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<QuotationRequestInterface[]>(
        [CACHE_KEY_QUOTATIONREQUESTS, page, pageSize],
        (quotationVendors = []) =>
          quotationVendors.map((data) =>
            data.id === updatedData.id ? updatedData : data
          )
      );

      return { previousQuotationRequest };
    },

    onSuccess: (savedData) => {
      ToastMessage.Success();
      queryClient.setQueryData<QuotationRequestInterface[]>(
        [CACHE_KEY_QUOTATIONREQUESTS, purchaseOrderId, page, pageSize],
        (quotationVendors) =>
          quotationVendors?.map((data) =>
            data.id === savedData.id ? savedData : data
          )
      );
    },

    onError: (error, updatedData, context) => {
      ToastMessage.Failed();
      if (!context) return;

      queryClient.setQueryData<QuotationRequestInterface[]>(
        [CACHE_KEY_QUOTATIONREQUESTS, purchaseOrderId, page, pageSize],
        context.previousQuotationRequest
      );
    },
  });
};

export default useUpdateQuotationRequest;
